import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from './supabase/client';

/**
 * Calling an Edge Function from the browser, with the failure actually
 * readable.
 *
 * `functions.invoke` reports any non-2xx as one opaque `FunctionsHttpError`,
 * so the message the function carefully wrote — "This link has expired", "That
 * client has already signed in" — is thrown away unless it is dug out of the
 * response. Every failure path in the invitation flow depends on the exact
 * words shown (design.md F5), so digging it out is not a nicety.
 */

export interface FunctionFailure {
  status: number;
  error: string;
}

export class FunctionError extends Error {
  readonly status: number;

  constructor(failure: FunctionFailure) {
    super(failure.error);
    this.name = 'FunctionError';
    this.status = failure.status;
  }
}

const UNREACHABLE = 'The server could not be reached. Check your connection and try again.';

export async function callFunction<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const result = await supabase.functions.invoke<T>(name, { body });
  const data = result.data;
  // supabase-js types `error` as `any`; treat it as unknown and narrow.
  const error: unknown = result.error;

  if (error) {
    // `context` is the raw Response, but supabase-js types it as `any`. Narrow
    // it here rather than trusting the declaration.
    const context: unknown = (error as { context?: unknown }).context;
    const response = context instanceof Response ? context : null;

    if (error instanceof FunctionsHttpError && response) {
      throw new FunctionError({
        status: response.status,
        error: await messageFrom(response),
      });
    }
    throw new FunctionError({ status: response?.status ?? 0, error: UNREACHABLE });
  }

  if (data === null) throw new FunctionError({ status: 0, error: UNREACHABLE });
  return data;
}

async function messageFrom(response: Response): Promise<string> {
  try {
    const payload: unknown = await response.json();
    if (payload !== null && typeof payload === 'object' && 'error' in payload) {
      const value: unknown = payload.error;
      if (typeof value === 'string' && value.length > 0) return value;
    }
  } catch {
    return UNREACHABLE;
  }
  return UNREACHABLE;
}
